import React, { useEffect, useState } from "react";
import Input from "../../../component/common/Input";
import { ReactComponent as DateIcon } from "../../../assets/calender.svg";
import { ReactComponent as ImageIcon } from "../../../assets/image-rectangle.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/trash.svg";
import Button from "../../../component/common/Button";
import CustomSelect from "../../../component/common/CustomSelect";
import DateSelect from "../../../component/common/DatePicker";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import formatDate from "../../../utils/dateFormater";
import { toast } from "react-toastify";
import httpServices from "../../../services/httpServices";
import { useNavigate } from "react-router-dom";
const eventTypeOptions = [
  { label: "Corporate", value: "Corporate", color: "#8000ff" },
  { label: "Wedding", value: "Wedding", color: "#ff4c61" },
  { label: "Concert", value: "Concert", color: "#3b82a0" },
  { label: "Private Party", value: "Private Party", color: "#f1a839" },
];

const eventSchema = Yup.object().shape({
  eventName: Yup.string()
    .required("Event name is required")
    .min(3, "Event name must be at least 3 characters"),
  eventDate: Yup.date()
    .required("Event date is required")
    .typeError("Invalid date format"),
  eventStartTime: Yup.string()
    .required("Event start time is required")
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format"),
  eventEndTime: Yup.string()
    .required("Event end time is required")
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format"),
  eventDescription: Yup.string()
    .required("Event description is required")
    .min(10, "Event description must be at least 10 characters"),

  eventPolicies: Yup.string().min(
    10,
    "Event policies must be at least 10 characters"
  ),
  eventMedia: Yup.array().of(Yup.string()),
  eventVenueDetails: Yup.object().shape({
    eventType: Yup.string().required("Event type is required"),
  }),
});

const EventDetails = ({ setActiveStep, setEventId }) => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [windowWidth, setWindowWidth] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventType, setEventType] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(eventSchema),
  });

  useEffect(() => {
    window.onresize = () => {
      setWindowWidth(window.innerWidth);
    };
  }, []);

  const onSubmit = async (data) => {
    if (images.length === 0) {
      toast.error("Please upload an image");
      return;
    }
    console.log("Form Data", data);
    try {
      const { response, error } = await httpServices.post(`/event/create`, {
        ...data,
        stepDone: 1,
        eventStatus: 1,
      });
      if (error) {
        throw error;
      }
      if (response.status === 201) {
        console.log("response", response);
        toast.success("Event created successfully");
        setActiveStep(1);
        setEventId(response.data.data.id);
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Something went wrong");
    }
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const newImage = {
        image: file,
        id: (Math.random() * 100).toFixed(0),
      };
      // Prevent duplicate uploads
      const isDuplicate = images.some((img) => img.image.name === file.name);
      if (!isDuplicate) {
        setImages((prevImages) => [...prevImages, newImage]);
      } else {
        alert("This image is already uploaded.");
      }
    }
  };

  const handleDelete = (id) => {
    setImages(images.filter((image) => image.id !== id));
  };
  const onError = (errors) => {
    console.log("Error:", errors);
  };

  return (
    <div className="p-3 my-4 sm:p-4 max-w-7xl bg-white rounded-[10px] min-h-[600px]">
      <h1 className="gradient-text font-semibold text-xl">Event Details</h1>

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="mt-5 gap-[14px] flex items-start justify-between flex-wrap lg:flex-nowrap">
          {/* Event Name Input */}
          <div className="flex flex-col w-full sm:w-[48%] lg:w-[25%]">
            <Input
              classes="w-full "
              label={"Event name*"}
              placeholder={"AJ Ballroom Section 2"}
              inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px]"
              lableClasses="text-sm lg:text-[16px]"
              {...register("eventName")}
            />
            {errors.eventName && (
              <p className="text-red-500 text-xs">{errors.eventName.message}</p>
            )}
          </div>

          {/* Event Type */}
          <div className="w-full sm:w-[48%] lg:w-[25%]">
            <label className="mb-2 text-sm lg:text-[16px] font-medium text-black block">
              What is your event type*
            </label>
            <CustomSelect
              options={eventTypeOptions}
              onChange={(value) => {
                setEventType(value);
                setValue("eventVenueDetails.eventType", value);
              }}
              value={eventType}
            />
            <p className="text-red-500  text-xs">
              {eventType === "" && errors.eventVenueDetails?.eventType?.message}
            </p>
          </div>

          {/* Event Date */}
          <div>
            <label className="mb-2 text-[12px] font-medium text-black block">
              When is the event?*
            </label>
            <DateSelect
              className="w-full py-3 outline-none border-none"
              placeholder="Select date"
              setSelectedDate={(date) => {
                setSelectedDate(formatDate(date));
                setValue("eventDate", formatDate(date));
                clearErrors("eventDate");
              }}
              selectedDate={selectedDate}
            />
            <p className="text-red-500 text-xs">{errors.eventDate?.message}</p>
          </div>

          {/* Event Time */}
          <div className="flex flex-col w-full sm:w-[48%] lg:w-[25%]">
            <Input
              classes="w-full"
              label={"When will the event start?*"}
              placeholder={"Enter event time"}
              inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px]"
              lableClasses="text-sm lg:text-[16px]"
              {...register("eventStartTime")}
            />
            {errors.eventStartTime && (
              <p className="text-red-500 text-xs">
                {errors.eventStartTime.message}
              </p>
            )}
          </div>
          <div className="flex flex-col w-full sm:w-[48%] lg:w-[25%]">
            <Input
              classes="w-full"
              label={"When will the event end?*"}
              placeholder={"Enter event end time"}
              inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px]"
              lableClasses="text-sm lg:text-[16px]"
              {...register("eventEndTime")}
            />
            {errors.eventEndTime && (
              <p className="text-red-500 text-xs">
                {errors.eventEndTime.message}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="mt-4 gap-[14px] flex items-center flex-wrap md:flex-nowrap">
          <div className="w-full md:w-[49%] relative">
            <label className="mb-2 text-sm lg:text-[16px] font-medium text-black block">
              Describe your event
            </label>
            <textarea
              placeholder={"Add event description"}
              className="pr-4 pl-5 py-3 text-black rounded bg-[#F5F6FA] w-full placeholder:text-500-gray text-sm focus:outline-none h-[150px] md:h-[243px]"
              {...register("eventDescription")}
            />
            <p className="text-red-500 text-xs">
              {errors?.eventDescription?.message}
            </p>

            {/* <span className="absolute text-sm right-[18px] bottom-[15px] text-[#4E5969]">
              100/500
            </span> */}
          </div>

          <div className="w-full md:w-[49%] relative">
            <label className="mb-2 text-sm lg:text-[16px] font-medium text-black block">
              Does your event have any policies
            </label>
            <textarea
              placeholder={"Add event policies"}
              className="pr-4 pl-5 py-3 text-black rounded bg-[#F5F6FA] w-full placeholder:text-500-gray text-sm focus:outline-none h-[150px] md:h-[243px]"
              {...register("eventPolicies")}
            />
            <p className="text-red-500 text-xs">
              {errors.eventPolicies?.message}
            </p>
            {/* <span className="absolute text-sm right-[18px] bottom-[20px] text-[#4E5969]">
              100/500
            </span> */}
          </div>
        </div>
        <div className="mt-[28px]">
          <h1 className="gradient-text font-semibold text-xl">Event Details</h1>

          <h3 className="text-[#24252D] font-medium text-base mt-[20px]">
            Add Images
          </h3>

          <div className="my-[10px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ">
            <div
              className=" h-[200px] bg-[#F5F6FA] relative border-2 border-gray-300 border-dashed rounded-lg flex items-center justify-center"
              id="dropzone"
            >
              <input
                type="file"
                className="absolute w-full h-full opacity-0 z-50"
                onChange={handleImage}
              />
              <div className="text-center flex flex-col items-center justify-center">
                {<ImageIcon />}

                <span className="text-[#515B6B] font-medium">Upload image</span>

                <p className="mt-[5px] text-[10px] text-[#515B6B]">
                  PNG, JPEG / 763×429
                </p>
                <p className="mt-[5px] text-[10px] text-[#515B6B]">
                  Min 1 image
                </p>
              </div>
            </div>

            {windowWidth < 768 ? (
              <div className="flex gap-2 flex-wrap">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="md:w-auto w-[80px] h-[80px] md:h-[200px] overflow-hidden relative rounded-lg "
                  >
                    <img
                      key={index}
                      src={URL.createObjectURL(image.image)}
                      className="h-full w-full object-cover"
                    />
                    <span
                      className="p-1 cursor-pointer bg-white rounded-[5px] absolute right-[10px] bottom-[10px]"
                      onClick={() => handleDelete(image.id)}
                    >
                      <DeleteIcon />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              images.length > 0 &&
              images.map((image, index) => (
                <div
                  key={index}
                  className="md:w-auto  md:h-[200px] overflow-hidden relative rounded-lg "
                >
                  <img
                    key={index}
                    src={URL.createObjectURL(image.image)}
                    className="h-full w-full  object-cover"
                  />
                  <span
                    className="p-1 cursor-pointer bg-white rounded-[5px] absolute right-[10px] bottom-[10px]"
                    onClick={() => handleDelete(image.id)}
                  >
                    <DeleteIcon />
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <Button
            content={"Save & Proceed"}
            type="submit"
            classes="py-[9px] px-[62px] btn-gradient"
          />
        </div>
      </form>
    </div>
  );
};

export default EventDetails;